import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs/Rx';

import { AzureApiService } from '../../services/azure-api.service';
import { DtoSaveResult } from '../../shared/models/DtoSaveResult';
import { DtoDeleteResult } from '../../shared/models/DtoDeleteResult';
import {DtoArticleList} from "./models/DtoArticleList";
import {DtoDiscountList} from "./models/DtoDiscountList";
import {DtoQuantityDiscountList} from "./models/DtoQuantityDiscountList";
import {DtoGiftCertificateList} from "./models/DtoGiftCertificateList";
import {DtoCureCreditList} from "./models/DtoCureCreditList";
import {DtoArticleBaseItems} from "./models/DtoArticleBaseItems";
import {DtoArticle} from "./models/DtoArticle";
import {plainToClass} from "class-transformer";
import {DtoTreatment} from "./models/DtoTreatment";
import {DtoTreatmentBaseItems} from "./models/DtoTreatmentBaseItems";
import {DtoTreatmentListResult} from "./models/DtoTreatmentListResult";
import {DtoTreatmentForCombi} from "./models/DtoTreatmentForCombi";
import {DtoTreatmentFilter} from "./models/DtoTreatmentFilter";
import {DtoArticleFilter} from "./models/DtoArticleFilter";
import {DtoArticleForCombi} from "./models/DtoArticleForCombi";
import {DtoDiscount} from "./models/DtoDiscount";
import {DtoDiscountBaseItems} from "./models/DtoDiscountBaseItems";
import {DtoQuantityDiscount} from "./models/DtoQuantityDiscount";
import {DtoGiftCertificate} from "./models/DtoGiftCertificate";
import {DtoCureCredit} from "./models/DtoCureCredit";
import {DtoPriceChangeLine} from "./models/DtoPriceChangeLine";
import {DtoPriceChange} from "./models/DtoPriceChange";
import {DtoPriceChangePlanned} from "./models/DtoPriceChangePlanned";
import {DtoSubscriptionTypeList} from "./models/DtoSubscriptionTypeList";
import {DtoSubscriptionType} from "./models/DtoSubscriptionType";
import {DtoSubscriptionTypeBaseItems} from "./models/DtoSubscriptionTypeBaseItems";
import {DtoTreatmentList} from "./models/DtoTreatmentList";

@Injectable()
export class SettingsCashregService {
    private apiUrlArticle: string;
    private apiUrlTreatment: string;
    private apiUrlDiscount: string;
    private apiUrlQuantityDiscount: string;
    private apiUrlGiftcertificate: string;
    private apiUrlCureCredit: string;
    private apiUrlSubscriptionType: string;
    private dtoArticleBaseItems: DtoArticleBaseItems;
    private dtoTreatmentBaseItems: DtoTreatmentBaseItems;
    private dtoDiscountBaseItems: DtoDiscountBaseItems;
    private dtoSubscriptionTypeBaseItems: DtoSubscriptionTypeBaseItems;
    private dtoSubscriptionTypeTreatmentsToUse: DtoTreatmentList[];

    constructor(private http: AzureApiService) {
        this.apiUrlArticle = 'article';
        this.apiUrlTreatment = 'treatment';
        this.apiUrlDiscount = 'discount';
        this.apiUrlQuantityDiscount = 'quantitydiscount';
        this.apiUrlGiftcertificate = 'giftcertificate';
        this.apiUrlCureCredit = 'curecredit';
        this.apiUrlSubscriptionType = 'subscription';
    }

    // ARTICLE
    public getArticles = (): Observable<DtoArticleList[]> => {
        return this.http.get<DtoArticleList[]>(`${this.apiUrlArticle}`);
    };

    public getArticleById = (id: number): Observable<DtoArticle> => {
        return this.http.get<DtoArticle>(`${this.apiUrlArticle}/${id}`);
    };

    public getArticleForCombi = (filter: DtoArticleFilter): Observable<DtoArticleForCombi[]> => {
        return this.http.post<DtoArticleForCombi[]>(`${this.apiUrlArticle}/filtered`, filter);
    };

    public getArticleBaseItems = (refresh: boolean): Observable<DtoArticleBaseItems> => {
        if (!this.dtoArticleBaseItems || refresh) {
            this.http.get<DtoArticleBaseItems>(`${this.apiUrlArticle}/baseitems`)
                .subscribe(response => this.dtoArticleBaseItems = response);
            return Observable.create(observer => {
                observer.next(this.dtoArticleBaseItems);
                observer.complete();
            });
        } else {
            return Observable.create(observer => {
                observer.next(this.dtoArticleBaseItems);
                observer.complete();
            });
        }
    };

    public saveArticle = (dtoObject: DtoArticle): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlArticle, dtoObject);
    };

    public deleteArticleById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlArticle}/${id}`);
    };

    public getArticleNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlArticle}/nextindex`);
    };

    public checkArticleIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlArticle}/indexavailable/${id}/${index}`);
    };

    public checkArticleDescriptionAvailable = (id: number, description: string): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlArticle}/descriptionavailable/${id}/${description}`);
    };

    public generateArticleBarcode = (): Observable<string> => {
        return this.http.get<string>(`${this.apiUrlArticle}/barcode`);
    };

    public articleHasFuturePriceChange = (kind: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlArticle}/hasfuturepricechange/${kind}`);
    };

    public generateArticlePriceChangeLines = (kind: number): Observable<DtoPriceChangeLine[]> => {
        return this.http.get<DtoPriceChangeLine[]>(`${this.apiUrlArticle}/pricechange/generatelist/${kind}`);
    };

    public getFutureArticlePriceChangedPlanned = (): Observable<DtoPriceChangePlanned[]> => {
        return this.http.get<DtoPriceChangePlanned[]>(`${this.apiUrlArticle}/pricechange/planned`);
    };

    public saveArticleTempPriceChanges = (dtoObject: DtoPriceChange): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(`${this.apiUrlArticle}/temppricechange`, dtoObject);
    };

    public saveArticlePriceChanges = (dtoObject: DtoPriceChange): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(`${this.apiUrlArticle}/pricechange`, dtoObject);
    };

    public deleteArticlePriceChangeById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlArticle}/pricechange/${id}`);
    };

    public getArticlePriceChangeById = (id: number): Observable<DtoPriceChange> => {
        return this.http.get<DtoPriceChange>(`${this.apiUrlArticle}/pricechange/${id}`);
    };

    // TREATMENT
    public getTreatments = (): Observable<DtoTreatmentListResult> => {
        return this.http.get<DtoTreatmentListResult>(`${this.apiUrlTreatment}`);
    };

    public getTreatmentById = (id: number): Observable<DtoTreatment> => {
        return this.http.get<DtoTreatment>(`${this.apiUrlTreatment}/${id}`);
    };

    public getTreatmentForCombi = (filter: DtoTreatmentFilter): Observable<DtoTreatmentForCombi[]> => {
        return this.http.post<DtoTreatmentForCombi[]>(`${this.apiUrlTreatment}/filtered`, filter);
    };

    public getTreatmentBaseItems = (refresh: boolean): Observable<DtoTreatmentBaseItems> => {
        if (!this.dtoTreatmentBaseItems || refresh) {
            this.http.get<DtoTreatmentBaseItems>(`${this.apiUrlTreatment}/baseitems`)
                .subscribe(response => this.dtoTreatmentBaseItems = response);
            return Observable.create(observer => {
                observer.next(this.dtoTreatmentBaseItems);
                observer.complete();
            });
        } else {
            return Observable.create(observer => {
                observer.next(this.dtoTreatmentBaseItems);
                observer.complete();
            });
        }
    };

    public saveTreatment = (dtoObject: DtoTreatment): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlTreatment, dtoObject);
    };

    public deleteTreatmentById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlTreatment}/${id}`);
    };

    public getTreatmentNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlTreatment}/nextindex`);
    };

    public checkTreatmentIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlTreatment}/indexavailable/${id}/${index}`);
    };

    public checkTreatmentDescriptionAvailable = (id: number, description: string): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlTreatment}/descriptionavailable/${id}/${description}`);
    };

    public generateTreatmentPriceChangeLines = (): Observable<DtoPriceChangeLine[]> => {
        return this.http.get<DtoPriceChangeLine[]>(`${this.apiUrlTreatment}/pricechange/generatelist`);
    };

    public treatmentHasFuturePriceChange = (): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlTreatment}/hasfuturepricechange`);
    };

    public getFutureTreatmentPriceChangedPlanned = (): Observable<DtoPriceChangePlanned[]> => {
        return this.http.get<DtoPriceChangePlanned[]>(`${this.apiUrlTreatment}/pricechange/planned`);
    };

    public saveTreatmentTempPriceChanges = (dtoObject: DtoPriceChange): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(`${this.apiUrlTreatment}/temppricechange`, dtoObject);
    };

    public saveTreatmentPriceChanges = (dtoObject: DtoPriceChange): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(`${this.apiUrlTreatment}/pricechange`, dtoObject);
    };

    public deleteTreatmentPriceChangeById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlTreatment}/pricechange/${id}`);
    };

    public getTreatmentPriceChangeById = (id: number): Observable<DtoPriceChange> => {
        return this.http.get<DtoPriceChange>(`${this.apiUrlTreatment}/pricechange/${id}`);
    };

    // DISCOUNT
    public getDiscounts = (): Observable<DtoDiscountList[]> => {
        return this.http.get<DtoDiscountList[]>(`${this.apiUrlDiscount}`);
    };

    public getDiscountById = (id: number): Observable<DtoDiscount> => {
        return this.http.get<DtoDiscount>(`${this.apiUrlDiscount}/${id}`);
    };

    public getDiscountBaseItems = (refresh: boolean): Observable<DtoDiscountBaseItems> => {
        if (!this.apiUrlDiscount || refresh) {
            this.http.get<DtoDiscountBaseItems>(`${this.apiUrlDiscount}/baseitems`)
                .subscribe(response => this.dtoDiscountBaseItems = response);
            return Observable.create(observer => {
                observer.next(this.dtoDiscountBaseItems);
                observer.complete();
            });
        } else {
            return Observable.create(observer => {
                observer.next(this.dtoDiscountBaseItems);
                observer.complete();
            });
        }
    };

    public saveDiscount = (dtoObject: DtoDiscount): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlDiscount, dtoObject);
    };

    public deleteDiscountById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlDiscount}/${id}`);
    };

    public getDiscountNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlDiscount}/nextindex`);
    };

    public checkDiscountIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlDiscount}/indexavailable/${id}/${index}`);
    };

    public checkDiscountDescriptionAvailable = (id: number, description: string): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlDiscount}/descriptionavailable/${id}/${description}`);
    };


    // QUANTITYDISCOUNT
    public getQuantityDiscounts = (): Observable<DtoQuantityDiscountList[]> => {
        return this.http.get<DtoQuantityDiscountList[]>(`${this.apiUrlQuantityDiscount}`);
    };

    public getQuantityDiscountById = (id: number): Observable<DtoQuantityDiscount> => {
        return this.http.get<DtoQuantityDiscount>(`${this.apiUrlQuantityDiscount}/${id}`);
    };

    public saveQuantityDiscount = (dtoObject: DtoQuantityDiscount): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlQuantityDiscount, dtoObject);
    };

    public deleteQuantityDiscountById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlQuantityDiscount}/${id}`);
    };

    public checkQuantityDiscountOverlap = (id:number, days: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlQuantityDiscount}/overlap/${id}/${days}`);
    };

    // GIFTCERTIFICATE
    public getGiftCertificates = (): Observable<DtoGiftCertificateList[]> => {
        return this.http.get<DtoGiftCertificateList[]>(`${this.apiUrlGiftcertificate}`);
    };

    public getGiftCertificateById = (id: number): Observable<DtoGiftCertificate> => {
        return this.http.get<DtoGiftCertificate>(`${this.apiUrlGiftcertificate}/${id}`);
    };

    public saveGiftCertificate = (dtoObject: DtoGiftCertificate): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlGiftcertificate, dtoObject);
    };

    public deleteGiftCertificateById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlGiftcertificate}/${id}`);
    };

    public getGiftCertificateNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlGiftcertificate}/nextindex`);
    };

    public checkGiftCertificateIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlGiftcertificate}/indexavailable/${id}/${index}`);
    };

    public checkGiftCertificateDescriptionAvailable = (id: number, description: string): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlGiftcertificate}/descriptionavailable/${id}/${description}`);
    };

    // CURECREDIT
    public getCureCredits = (): Observable<DtoCureCreditList[]> => {
        return this.http.get<DtoCureCreditList[]>(`${this.apiUrlCureCredit}`);
    };

    public getCureCreditById = (id: number): Observable<DtoCureCredit> => {
        return this.http.get<DtoCureCredit>(`${this.apiUrlCureCredit}/${id}`);
    };

    public saveCureCredit = (dtoObject: DtoCureCredit): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlCureCredit, dtoObject);
    };

    public deleteCureCreditById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlCureCredit}/${id}`);
    };

    public getCureCreditNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlCureCredit}/nextindex`);
    };

    public checkCureCreditIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlCureCredit}/indexavailable/${id}/${index}`);
    };

    public checkCureCreditDescriptionAvailable = (id: number, description: string): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlCureCredit}/descriptionavailable/${id}/${description}`);
    };

    // SUBSCRIPTIONTYPES
    public getSubscriptionTypes = (): Observable<DtoSubscriptionTypeList[]> => {
        return this.http.get<DtoSubscriptionTypeList[]>(`${this.apiUrlSubscriptionType}`);
    };

    public getSubscriptionTypeById = (id: number): Observable<DtoSubscriptionType> => {
        return this.http.get<DtoSubscriptionType>(`${this.apiUrlSubscriptionType}/${id}`);
    };

    public saveSubscriptionType = (dtoObject: DtoSubscriptionType): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlSubscriptionType, dtoObject);
    };

    public deleteSubscriptionTypeById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlSubscriptionType}/${id}`);
    };

    public getSubscriptionTypeNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlSubscriptionType}/nextindex`);
    };

    public checkSubscriptionTypeIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlSubscriptionType}/indexavailable/${id}/${index}`);
    };

    public checkSubscriptionTypeDescriptionAvailable = (id: number, description: string): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlSubscriptionType}/descriptionavailable/${id}/${description}`);
    };

    public getSubscriptionTypeBaseItems = (refresh: boolean): Observable<DtoSubscriptionTypeBaseItems> => {
        if (!this.dtoSubscriptionTypeBaseItems || refresh) {
            this.http.get<DtoSubscriptionTypeBaseItems>(`${this.apiUrlSubscriptionType}/baseitems`)
                .subscribe(response => this.dtoSubscriptionTypeBaseItems = response);
            return Observable.create(observer => {
                observer.next(this.dtoSubscriptionTypeBaseItems);
                observer.complete();
            });
        } else {
            return Observable.create(observer => {
                observer.next(this.dtoSubscriptionTypeBaseItems);
                observer.complete();
            });
        }
    };

    public getSubscriptionTypeTreatmentsToUse = (refresh: boolean): Observable<DtoTreatmentList[]> => {
        if (!this.dtoSubscriptionTypeTreatmentsToUse || refresh) {
            this.http.get<DtoTreatmentList[]>(`${this.apiUrlSubscriptionType}/treatments`)
                .subscribe(response => this.dtoSubscriptionTypeTreatmentsToUse = response);
            return Observable.create(observer => {
                observer.next(this.dtoSubscriptionTypeTreatmentsToUse);
                observer.complete();
            });
        } else {
            return Observable.create(observer => {
                observer.next(this.dtoSubscriptionTypeTreatmentsToUse);
                observer.complete();
            });
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/settings/settings-cashreg/settings-cashreg.service.ts